import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { create } from "zustand";

import type { AstGrepMatchSchemaType } from "@acme/validators";
import { toast } from "@acme/ui/toast";

import { useTRPC } from "~/trpc/react";
import { buildRules } from "./ast-grep-hooks";
import { useStudioStore } from "./use-studio-store";

interface FileSource {
  file: string;
  source?: string;
  matches: AstGrepMatchSchemaType[];
}

interface AstGrepRemoteRunStore {
  files: Map<string, FileSource>;
  setFiles: (files: Map<string, FileSource>) => void;
  clearAll: () => void;
  getFiles: () => Map<string, FileSource>;
  openFiles: string[];
  addOpenFile: (filePath: string, area?: string) => void;
  removeOpenFile: (file: string) => void;
  reorderOpenFiles: (files: string[]) => void;
  matchedStats: {
    files: number;
    matches: number;
  };
  setMatchedStats: (stats: { files: number; matches: number }) => void;
}

const useAstGrepRemoteRunStoreBase = create<AstGrepRemoteRunStore>(
  (set, get) => ({
    files: new Map(),
    setFiles: (files) =>
      set((state) => ({
        files,
        openFiles: state.openFiles.filter((file) => files.has(file)),
      })),
    clearAll: () => set({ files: new Map(), openFiles: [] }),
    getFiles: () => get().files,
    openFiles: [],
    addOpenFile: (filePath: string, area?: string) =>
      set((state) => {
        const fileWithArea = area ? `${filePath}@${area}` : filePath;
        const existingFileIndex = state.openFiles.findIndex((file) =>
          file.startsWith(filePath),
        );
        if (existingFileIndex !== -1) {
          const updatedOpenFiles = [...state.openFiles];
          updatedOpenFiles[existingFileIndex] = fileWithArea;
          return { openFiles: updatedOpenFiles };
        }
        return {
          openFiles: [...state.openFiles, fileWithArea],
        };
      }),
    removeOpenFile: (file) =>
      set((state) => ({
        openFiles: state.openFiles.filter((f) => f !== file),
      })),
    reorderOpenFiles: (files) => {
      set({ openFiles: files });
    },
    matchedStats: {
      files: 0,
      matches: 0,
    },
    setMatchedStats: (stats) => set({ matchedStats: stats }),
  }),
);

export const useAstGrepRemoteRunFiles = () =>
  useAstGrepRemoteRunStoreBase((state) => state.files);

const useAstGrepRemoteRunGetFiles = () =>
  useAstGrepRemoteRunStoreBase((state) => state.getFiles);

const useAstGrepRemoteRunSetFiles = () =>
  useAstGrepRemoteRunStoreBase((state) => state.setFiles);

const useAstGrepRemoteRunClearAll = () =>
  useAstGrepRemoteRunStoreBase((state) => state.clearAll);

export const useAstGrepRemoteRunOpenFiles = () =>
  useAstGrepRemoteRunStoreBase((state) => state.openFiles);

export const useAstGrepRemoteRunAddOpenFile = () =>
  useAstGrepRemoteRunStoreBase((state) => state.addOpenFile);

export const useAstGrepRemoteRunRemoveOpenFile = () =>
  useAstGrepRemoteRunStoreBase((state) => state.removeOpenFile);

export const useAstGrepRemoteRunReorderOpenFiles = () =>
  useAstGrepRemoteRunStoreBase((state) => state.reorderOpenFiles);

const useAstGrepRemoteRunSetMatchedStats = () =>
  useAstGrepRemoteRunStoreBase((state) => state.setMatchedStats);

export const useAstGrepRemoteRunMatchedStats = () =>
  useAstGrepRemoteRunStoreBase((state) => state.matchedStats);

export const useAstGrepRemoteRun = () => {
  const api = useTRPC();

  const { mutateAsync, isPending } = useMutation(
    api.studio.runAstGrep.mutationOptions(),
  );
  const selectedRepo = useStudioStore((s) => s.selectedRepositories?.[0]);
  const getCurrentState = useStudioStore((s) => s.getCurrentState);
  const [isRunning, setIsRunning] = useState(false);

  const getFiles = useAstGrepRemoteRunGetFiles();
  const setFiles = useAstGrepRemoteRunSetFiles();
  const clearAll = useAstGrepRemoteRunClearAll();
  const setMatchedStats = useAstGrepRemoteRunSetMatchedStats();
  const runAstGrep = useCallback(async () => {
    if (!selectedRepo) {
      toast.error("No repository selected");
      return;
    }
    const rules = buildRules(getCurrentState());

    setIsRunning(true);
    try {
      let filesCount = 0;
      let matchesCount = 0;

      const eventStream = await mutateAsync({
        repository: selectedRepo,
        rules,
      });
      clearAll();

      for await (const batch of eventStream) {
        if (!batch) {
          continue;
        }

        const newMap = new Map(getFiles());

        for (const message of batch.messages) {
          switch (message.type) {
            case "match": {
              if (newMap.has(message.match.file)) {
                newMap.get(message.match.file)?.matches.push(message.match);
              } else {
                newMap.set(message.match.file, {
                  file: message.match.file,
                  matches: [message.match],
                });
              }
              matchesCount++;
              break;
            }
            case "fileContent":
              {
                const currentFileEntry = newMap.get(message.file);
                if (currentFileEntry) {
                  currentFileEntry.source = message.content;
                } else {
                  newMap.set(message.file, {
                    file: message.file,
                    source: message.content,
                    matches: [],
                  });
                }
                filesCount++;
              }
              break;
            case "error":
              toast.error(message.error);
              break;
          }
        }

        setMatchedStats({ files: filesCount, matches: matchesCount });
        setFiles(newMap);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error running the Codemod on cloud");
    } finally {
      setIsRunning(false);
    }
  }, [
    selectedRepo,
    getCurrentState,
    mutateAsync,
    clearAll,
    getFiles,
    setMatchedStats,
    setFiles,
  ]);
  return { runAstGrep, isPending: isRunning || isPending };
};
